import { MobileModal } from '@components/MobileModal/MobileModal';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { FileWithDuration } from '@lib/hooks/useFileMetadataExtraction';
import { useTopUp } from '@lib/hooks/useTopUp';
import type { IngestEstimate } from '@lib/utils/ingestEstimate';
import { OwnerAffordability } from '@lib/utils/ownerAffordability';
import { pluralize } from '@lib/utils/units';
import { Tooltip } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import {
  BaseModal,
  BaseModalRef,
} from '@pages/Search/components/MomentGrid/components/BaseModal/BaseModal';
import { useModalRegistration } from '@stores/ModalStore';
import { trackingStore } from '@stores/Tracking';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'throttle-debounce';
import css from './ChooseFileUploadModal.module.scss';
import {
  FileUploadDataGrid,
  FileUploadDataGridRef,
} from './ChooseMediaModal/components/FileUploadDataGrid';
import { ChooseMediaModalFooter } from './ChooseMediaModalFooter';
import { MobileSelectableMediaList, SelectableMediaItem } from './MobileSelectableMediaList';
import { getUploadDisabledState } from './uploadDisabledState';

export type { FileWithDuration } from '@lib/hooks/useFileMetadataExtraction';

interface ChooseFileUploadModalProps {
  open: boolean;
  filesWithMetadata: FileWithDuration[];
  filesWithFingerprints: Map<File, string>;
  existingUniqueIds: Record<string, string>;
  selectedIndexes: string[];
  onSelectionChange: (indexes: string[]) => void;
  onCancel: () => void;
  onAdvanceToConfirm: () => void;
  creditCost: number;
  creditsLeft: number | null;
  creditsAfterProcessing: number | null;
  storageLeft: number;
  storageAfterProcessing: number;
  ingestEstimate: IngestEstimate;
  isUploading: boolean;
  /** False while files are still being fingerprinted (duplicate detection). The
   * Add button is disabled until this completes. */
  isFingerprintingComplete: boolean;
  /**
   * True when the target library is billed to another account, so none of the credit/storage
   * figures above describe the wallet being charged. Swaps the footer stats for the owner
   * notice, drops the top-up CTA, and stops the user's own balance from gating Add.
   */
  billedToAnotherAccount: boolean;
  /** The owner's display name, when the backend gave one. Only read while the flag above is set. */
  ownerName?: string;
  /** Replaces the balance gate the flag above removes. See `ownerAffordability.ts`. */
  ownerAffordability?: OwnerAffordability;
}

export const ChooseFileUploadModal = observer(
  ({
    open,
    filesWithMetadata,
    filesWithFingerprints,
    existingUniqueIds,
    selectedIndexes,
    onSelectionChange,
    onCancel,
    onAdvanceToConfirm,
    creditCost,
    creditsLeft,
    creditsAfterProcessing,
    storageLeft,
    storageAfterProcessing,
    ingestEstimate,
    isUploading,
    isFingerprintingComplete,
    billedToAnotherAccount,
    ownerName,
    ownerAffordability,
  }: ChooseFileUploadModalProps) => {
    const isMobile = useIsMobile();
    const baseModalRef = useRef<BaseModalRef>(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const gridRef = useRef<FileUploadDataGridRef>(null);

    // 'choose-file-upload' was declared in ModalName and allow-listed in dragDropAllowedModals from
    // the start, but nothing ever registered it — so that drag-drop policy was reading a state
    // nothing set. Registering it is what lets callers tell "the file picker is up" from "nothing
    // is open".
    useModalRegistration('choose-file-upload', open);

    // Sync open prop with modal visibility
    useEffect(() => {
      if (open) {
        if (isMobile) {
          setMobileOpen(true);
          trackingStore.fileSelectModalOpened({
            totalFiles: filesWithMetadata.length,
            totalSizeBytes: filesWithMetadata.reduce((sum, f) => sum + f.size, 0),
          });
        } else {
          baseModalRef.current?.open();
        }
      } else {
        if (isMobile) {
          setMobileOpen(false);
        } else {
          baseModalRef.current?.close();
        }
      }
    }, [open]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const trackSelectionDebounced = useCallback(
      debounce(300, (selectedCount: number, totalFiles: number) => {
        trackingStore.fileSelectionChanged({ selectedCount, totalFiles });
      }),
      [],
    );

    const handleSelectionChange = (newSelection: string[]) => {
      onSelectionChange(newSelection);
      trackSelectionDebounced(newSelection.length, filesWithMetadata.length);
    };

    const handleClose = () => {
      trackingStore.fileSelectModalClosed({
        selectedCount: selectedIndexes.length,
        totalFiles: filesWithMetadata.length,
      });
      if (isMobile) setMobileOpen(false);
      onCancel();
    };

    const handleCancelClick = () => {
      trackingStore.chooseFileUploadCancelled({
        selectedCount: selectedIndexes.length,
        totalFiles: filesWithMetadata.length,
      });
      if (isMobile) {
        handleClose();
      } else {
        baseModalRef.current?.close();
      }
    };

    const handleAddClick = () => {
      trackingStore.chooseFileUploadConfirmed({
        selectedCount: selectedIndexes.length,
        totalFiles: filesWithMetadata.length,
        totalDurationSeconds: ingestEstimate.durationSeconds,
        totalSizeBytes: filesWithMetadata
          .filter((_, i) => selectedIndexes.includes(i.toString()))
          .reduce((sum, f) => sum + f.size, 0),
        creditsCost: creditCost,
        creditsAvailable: creditsLeft ?? 0,
      });
      onAdvanceToConfirm();
    };

    const baseUploadDisabled = getUploadDisabledState(
      creditsAfterProcessing,
      storageAfterProcessing,
      selectedIndexes.length,
      isUploading,
      { billedToAnotherAccount, ownerAffordability, ownerName },
    );

    // Files still being fingerprinted (duplicate detection) — we don't wait on
    // thumbnails, only dedup. Block the Add button until it's done so we never
    // upload a file before knowing whether it's already in the library.
    const pendingCount = filesWithMetadata.reduce(
      (n, file) => (file.metadataExtracted ? n : n + 1),
      0,
    );
    const isUploadDisabled = !isFingerprintingComplete
      ? { value: true, reason: 'Checking these files for duplicates. You can add them once done.' }
      : baseUploadDisabled;
    const addButtonLabel = !isFingerprintingComplete
      ? `Loading ${pluralize(pendingCount, 'file')}...`
      : `Add ${pluralize(selectedIndexes.length, 'file')}`;

    // Nothing to top up when the owner pays: buying credits on this account wouldn't unblock
    // an upload it isn't charged for. Passing `insufficient: false` and omitting `storage`
    // leaves `needsAction` false, which hides both the CTA and the secondary-button styling.
    const topUp = useTopUp({
      creditCost,
      currentCredits: creditsLeft ?? 0,
      insufficient:
        !billedToAnotherAccount && creditsAfterProcessing !== null && creditsAfterProcessing <= 0,
      source: 'file-upload-selection',
      ...(billedToAnotherAccount
        ? {}
        : {
            storage: {
              afterProcessing: storageAfterProcessing,
              duration: ingestEstimate.durationSeconds,
            },
          }),
    });

    // Convert files to selectable list items for mobile
    const mobileListItems: SelectableMediaItem[] = useMemo(
      () =>
        filesWithMetadata.map((file, index) => {
          const fingerprint = filesWithFingerprints.get(file);
          const isDuplicate = fingerprint ? fingerprint in existingUniqueIds : false;
          return {
            id: index.toString(),
            title: file.name,
            thumbnail: file.thumbnail,
            duration: file.duration,
            status: isDuplicate ? 'in-library' : null,
            // Skeleton until this file's metadata batch finishes (even if its
            // fingerprint errored — it still gets included).
            isPending: !file.metadataExtracted,
          };
        }),
      [filesWithMetadata, filesWithFingerprints, existingUniqueIds],
    );

    const handleMobileToggle = (id: string) => {
      const item = mobileListItems.find((i) => i.id === id);
      if (item?.status === 'in-library') return;
      const newSelection = selectedIndexes.includes(id)
        ? selectedIndexes.filter((s) => s !== id)
        : [...selectedIndexes, id];
      handleSelectionChange(newSelection);
    };

    const selectableIds = useMemo(
      () => mobileListItems.filter((item) => item.status === null).map((item) => item.id),
      [mobileListItems],
    );

    const handleMobileSelectAll = () => handleSelectionChange(selectableIds);
    const handleMobileDeselectAll = () => handleSelectionChange([]);

    const footerContent = (
      <div className={css.footer}>
        <ChooseMediaModalFooter
          nSelected={{
            total: selectedIndexes.length,
            filteredOut: 0,
            alreadyInLibrary: 0,
          }}
          creditStats={{
            current: creditsLeft ?? 0,
            cost: creditCost,
            left: creditsAfterProcessing ?? -1,
          }}
          storageStats={{
            current: storageLeft,
            cost: ingestEstimate.durationSeconds,
            left: storageAfterProcessing,
          }}
          hasUnavailableInfo={ingestEstimate.hasUnknownDuration}
          billedToAnotherAccount={billedToAnotherAccount}
          ownerName={ownerName}
          ownerAffordability={ownerAffordability}
        />
        <span className={css.buttonContainer}>
          <ActionButton variant="secondary" onClick={handleCancelClick} padding={32}>
            Cancel
          </ActionButton>
          {topUp.needsAction && topUp.buttonLabel && (
            <Tooltip title={topUp.tooltip ?? ''} placement="top" arrow>
              <span>
                <ActionButton onClick={topUp.handleTopUp} disabled={topUp.isToppingUp} padding={32}>
                  {topUp.buttonLabel}
                </ActionButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title={isUploadDisabled.reason} placement="top" arrow>
            <span>
              <ActionButton
                onClick={handleAddClick}
                disabled={isUploadDisabled.value}
                variant={topUp.needsAction ? 'secondary' : undefined}
                padding={32}
              >
                {addButtonLabel}
              </ActionButton>
            </span>
          </Tooltip>
        </span>
      </div>
    );

    const mobileFooter = (
      <div className={css.mobileFooter}>
        <div className={css.mobileFooterRow}>
          <ChooseMediaModalFooter
            mobile
            nSelected={{
              total: selectedIndexes.length,
              filteredOut: 0,
              alreadyInLibrary: 0,
            }}
            creditStats={{
              current: creditsLeft ?? 0,
              cost: creditCost,
              left: creditsAfterProcessing ?? -1,
            }}
            storageStats={{
              current: storageLeft,
              cost: ingestEstimate.durationSeconds,
              left: storageAfterProcessing,
            }}
            hasUnavailableInfo={ingestEstimate.hasUnknownDuration}
            billedToAnotherAccount={billedToAnotherAccount}
            ownerName={ownerName}
            ownerAffordability={ownerAffordability}
          />
        </div>
        <div className={css.mobileFooterButtons}>
          {topUp.needsAction && topUp.buttonLabel ? (
            <ActionButton onClick={topUp.handleTopUp} disabled={topUp.isToppingUp} fullWidth>
              {topUp.buttonLabel}
            </ActionButton>
          ) : (
            <ActionButton variant="secondary" onClick={handleCancelClick} fullWidth>
              Cancel
            </ActionButton>
          )}
          <ActionButton onClick={handleAddClick} disabled={isUploadDisabled.value} fullWidth>
            {addButtonLabel}
          </ActionButton>
        </div>
      </div>
    );

    return isMobile ? (
      <MobileModal
        open={mobileOpen}
        onClose={handleClose}
        title="Select files"
        footer={mobileFooter}
        rawContent
      >
        <MobileSelectableMediaList
          items={mobileListItems}
          selectedIds={selectedIndexes}
          onToggle={handleMobileToggle}
          onSelectAll={handleMobileSelectAll}
          onDeselectAll={handleMobileDeselectAll}
        />
      </MobileModal>
    ) : (
      <BaseModal
        ref={baseModalRef}
        title="Select Files"
        onOpen={() => {
          trackingStore.fileSelectModalOpened({
            totalFiles: filesWithMetadata.length,
            totalSizeBytes: filesWithMetadata.reduce((sum, f) => sum + f.size, 0),
          });
        }}
        onClose={handleClose}
        footerContent={footerContent}
        className={css.baseModal}
      >
        <FileUploadDataGrid
          ref={gridRef}
          files={filesWithMetadata}
          selectedIndexes={selectedIndexes}
          onSelectionChange={handleSelectionChange}
          existingUniqueIds={existingUniqueIds}
          filesWithFingerprints={filesWithFingerprints}
        />
      </BaseModal>
    );
  },
);
